/**
 * @file typo-base.ts
 * @description Размер «как у роли» частей элемента — то, что витрина ставит,
 * пока у части нет своего числа (phase19.16, партия SB120, этап 3а;
 * uiux-sb120-stage3 §2.1, §3; правила section.css витрины).
 *
 * Нужен полю «Размер» (`FontField` → «↺ 48 px (H1)») и строке «на телефоне
 * N px»: заголовки — уровень × «Размер заголовков» темы (ПК 48/32/20,
 * телефон 32/24/18), надпись — по основе (капсом 14, акцентом 24),
 * остальное — постоянные размеры витрины. Плитка: название — по ширине
 * плитки 15–20 px (шкала темы его не трогает).
 *
 * Реестр по видам с `typo` (OCP): новый вид в `TYPO_PART_KEYS` без строки
 * здесь — ошибка tsc. Ступени [H3][H2][H1] — по шкале темы.
 *
 * Этап 3б (классы витрины, сверено с apps/web 2026-10-18): категории —
 * подпись `text-sm` 14; статьи — рубрика `sec-eyebrow-caps text-xs` 12 с
 * разрядкой темы, название `sec-h3`, анонс `sec-text` 15; отзывы —
 * заголовок `sec-h3`, текст 15; объявления — заголовок карточки `sec-h3`,
 * текст полосы `text-sm` 14, у карточек и списка `sec-text` 15; рассылка —
 * подписи формы `text-sm` 14.
 */

import { phoneFontSize, type CaptionPreset, type SiteTheme, type TypoElementKind, type TypoPart } from "@chaiberi/shared";

import type { NumberPreset } from "../../components/site-fields/NumberPresets";
import { formatNumber } from "../site-fields/number-input";

/** Размер части «как у роли». */
export interface TypoBase {
  /** px на ПК (с 768). */
  desktop: number;
  /** px на телефоне (до 768). */
  phone: number;
  /** Чей размер: «H1», «капсом». */
  label: string;
  /** Короткая подпись вместо «N px (label)», если одного числа нет: «по ширине плитки» (15–20 px). */
  text?: string;
  /** Разрядка «как у роли», em. */
  tracking?: number;
}

const H_DESKTOP: Readonly<Record<1 | 2 | 3, number>> = { 1: 48, 2: 32, 3: 20 };
const H_PHONE: Readonly<Record<1 | 2 | 3, number>> = { 1: 32, 2: 24, 3: 18 };

/** Шкала заголовков темы; нет темы — 1. */
export function headingScale(theme: SiteTheme | null): number {
  return theme?.type.headingScale ?? 1;
}

const round2 = (v: number): number => Math.round(v * 100) / 100;
const asLevel = (level: unknown): 1 | 2 | 3 => (level === 1 || level === 3 ? level : 2);
const fixed = (px: number, label: string, phone = px): TypoBase => ({ desktop: px, phone, label });

/** Заголовок уровня × шкала темы. */
export function levelBase(level: unknown, theme: SiteTheme | null): TypoBase {
  const l = asLevel(level);
  const s = headingScale(theme);
  return { desktop: round2(H_DESKTOP[l] * s), phone: round2(H_PHONE[l] * s), label: `H${l}` };
}

/** Надпись (и надпись на фото) по основе: капсом — 14 px с разрядкой темы, акцентом — 24 px. */
export function captionBase(preset: unknown, theme: SiteTheme | null): TypoBase {
  if ((preset as CaptionPreset) === "script") return fixed(24, "акцентом");
  return { ...fixed(14, "капсом"), tracking: theme?.type.eyebrowTracking ?? 0 };
}

type BaseFn = (owner: Readonly<Record<string, unknown>>, key: string, theme: SiteTheme | null) => TypoBase;

const TEXT: BaseFn = () => fixed(15, "текст");

/** Размер «как у роли» по виду и части. */
export const TYPO_BASE: Readonly<Record<TypoElementKind, BaseFn>> = {
  heading: (el, _key, theme) => levelBase(el.level, theme),
  eyebrow: (el, _key, theme) => captionBase(el.preset, theme),
  text: TEXT,
  button: () => fixed(14, "кнопка"),
  table: TEXT,
  faq: TEXT,
  contacts: TEXT,
  tiles: (_el, key) => (key === "title" ? { desktop: 20, phone: 15, label: "плитка", text: "по ширине плитки" } : TEXT({}, key, null)),
  stats: (el, key) => {
    if (key !== "number") return fixed(14, "подпись");
    return el.size === "l" ? fixed(60, "крупный", 36) : fixed(36, "обычный", 30);
  },
  breadcrumbs: () => fixed(14, "крошки"),
  // Элементы товара — классы витрины (ProductTextElements / ProductDescriptionElement), одинаковы на телефоне и ПК.
  "product-short": () => fixed(14, "текст"),
  "product-title": (_el, key) => (key === "title" ? fixed(24, "название") : key === "category" ? fixed(24, "категория") : fixed(14, "рейтинг")),
  "product-description": (_el, key) => (key === "label" ? fixed(12, "подпись") : fixed(14, "текст")),
  // Этап 3б.
  categories: () => fixed(14, "подпись"),
  articles: (_el, key, theme) => {
    if (key === "title") return levelBase(3, theme);
    if (key === "category") return { ...fixed(12, "рубрика"), tracking: theme?.type.eyebrowTracking ?? 0 };
    return fixed(15, "текст");
  },
  reviews: (_el, key, theme) => (key === "title" ? levelBase(3, theme) : fixed(15, "текст")),
  announcements: (el, key, theme) => {
    if (key === "title") return levelBase(3, theme);
    return el.layout === "strip" ? fixed(14, "полоса") : fixed(15, "текст");
  },
  newsletter: () => fixed(14, "подписи формы"),
};

/** Шапка раздела: надпись 14, заголовок по уровню, ссылка 14. */
export function headerTypoBase(header: { level: unknown }, key: string, theme: SiteTheme | null): TypoBase {
  if (key === "title") return levelBase(header.level, theme);
  return key === "eyebrow" ? captionBase("caps", theme) : fixed(14, "ссылка");
}

/** Ступени размера заголовка по шкале темы: [H3 20][H2 32][H1 48]. */
export function headingSizePresets(theme: SiteTheme | null): NumberPreset[] {
  return ([3, 2, 1] as const).map((l) => {
    const value = round2(H_DESKTOP[l] * headingScale(theme));
    return { label: `H${l} ${formatNumber(value, 2)}`, value };
  });
}

/** Размер на телефоне: своё → по правилу от своего ПК → как у роли. */
export function phoneSizeOf(part: TypoPart | undefined, base: TypoBase): { value: number; own: boolean } {
  if (part?.sizePhone !== undefined) return { value: part.sizePhone, own: true };
  if (part?.size !== undefined) return { value: phoneFontSize(part.size), own: false };
  return { value: base.phone, own: false };
}

/** «на телефоне 36 px» / «на телефоне своё: 30 px». */
export function phoneSizeSummary(part: TypoPart | undefined, base: TypoBase): string {
  const phone = phoneSizeOf(part, base);
  return phone.own ? `на телефоне своё: ${formatNumber(phone.value, 2)} px` : `на телефоне ${formatNumber(phone.value, 2)} px`;
}
